/**
 * Contract API Service
 * Handles all contract-related API calls including workflow operations
 */

import { API_BASE_URL as API_BASE } from '../config/api';
import { isNativeMobileApp } from '../utils/mobileApp';

export interface Contract {
  id: number;
  property_id: number;
  tenant_id: number;
  landlord_id: number;
  broker_id?: number;
  contract_type: string;
  status: string;
  start_date: string;
  end_date?: string;
  amount: number;
  monthly_payment?: number;
  deposit?: number;
  rent_payment_day?: number;
  total_payments: number;
  completed_payments: number;
  tenant_signed: boolean;
  landlord_signed: boolean;
  broker_signed: boolean;
  tenant_signed_at?: string;
  landlord_signed_at?: string;
  tenant_signature_image?: string | null;
  landlord_signature_image?: string | null;
  notes?: string;
  created_at: string;
  updated_at: string;
  // Workflow fields
  workflow_scenario?: string;
  workflow_current_step?: string;
  check_in_status?: string;
  key_handover_status?: string;
  key_handover_confirmed_by_tenant?: boolean;
  key_handover_confirmed_by_landlord?: boolean;
  key_handover_date?: string;
  check_in_completed_at?: string;
  check_in_metadata?: Record<string, any>; // Check-in workflow metadata
  // Payment tracking
  total_paid?: number;
  // Property info
  payment_term?: number; // Kỳ thanh toán (months per period) từ property
  // Payment account info
  transfer_content?: string; // Nội dung chuyển khoản
  bank_name?: string; // Tên ngân hàng
  account_number?: string; // Số tài khoản
  account_holder?: string; // Chủ tài khoản
  tenant_name?: string; // Tên người thuê
  property_name?: string;
  full_address?: string | null;
  room_number?: string;
  tenant_cccd?: string;
  tenant_date_of_birth?: string;
  tenant_address?: string;
  tenant_phone?: string;
  template_document?: Record<string, string> | null;
  // Countdown fields
  days_remaining?: number;
  countdown_status?: string; // 'danger' (<7 days), 'warning' (7-30), 'normal' (>30), 'expired'
  // Termination fields
  termination_status?: string;
  termination_requested_at?: string;
  termination_approved_at?: string;
  early_termination?: boolean;
  actual_end_date?: string;
  scheduled_cancel_date?: string | null;
  deposit_refund_amount?: number;
  compensation_amount?: number;
}

export interface ContractPayment {
  id: number;
  contract_id: number;
  user_id: number;
  payment_date: string;
  amount: number;
  payment_method?: string;
  payment_status: string;
  transaction_id?: string;
  bank_reference?: string;
  notes?: string;
  created_at: string;
  updated_at: string;
}

export interface WorkflowStatus {
  contract_id: number;
  workflow_scenario?: string;
  workflow_current_step?: string;
  check_in_status?: string;
  key_handover_status?: string;
  has_completed_payment: boolean;
  contract_signed: boolean;
  key_handover_confirmed: boolean;
  total_required?: number;
  total_paid?: number;
  is_full_payment?: boolean;
  is_partial_payment?: boolean;
}

class ContractApiService {
  private shouldClearAuthTokens(errorMessage: string): boolean {
    const normalized = (errorMessage || '').toLowerCase();
    const authTokenErrors = [
      'token không hợp lệ',
      'token khong hop le',
      'đã hết hạn',
      'da het han',
      'invalid authentication token',
      'token is invalid',
      'token expired',
      'signature has expired',
      'could not validate credentials',
      'not authenticated',
      'unauthorized'
    ];

    return authTokenErrors.some((msg) => normalized.includes(msg));
  }

  private getAuthHeaders(): HeadersInit {
    // Try both 'auth_token' and 'token' for compatibility
    const token = localStorage.getItem('auth_token') || localStorage.getItem('token');
    
    if (!token) {
      console.error('[ContractApi] No authentication token found in localStorage');
      throw new Error('Authentication token not found. Please login again.');
    }
    
    return {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    };
  }
  
  private async handleAuthError(response: Response): Promise<never> {
    if (response.status === 401) {
      // Try to read backend error and only clear token if it's truly invalid/expired.
      const errorText = await response.text();
      let errorMessage = 'Authentication failed. Please login again.';
      
      try {
        const errorJson = JSON.parse(errorText);
        errorMessage = errorJson.detail || errorMessage;
      } catch {
        errorMessage = errorText || errorMessage;
      }

      if (this.shouldClearAuthTokens(errorMessage) && !isNativeMobileApp()) {
        localStorage.removeItem('auth_token');
        localStorage.removeItem('token');
      }
      
      throw new Error(errorMessage);
    }
    
    const errorText = await response.text();
    throw new Error(errorText || `Request failed with status ${response.status}`);
  }

  /**
   * Create a new contract
   */
  async createContract(contractData: {
    property_id: number;
    tenant_id?: number;
    tenant_name?: string;
    contract_name?: string;
    landlord_id?: number;  // Optional - backend will determine based on property owner account_type
    broker_id?: number;    // Optional - backend will determine based on property owner account_type
    contract_type?: string;
    start_date: string;
    end_date?: string;
    amount: number;
    monthly_payment?: number;
    deposit?: number | string;
    total_payments?: number;
    notes?: string;
    tenant_cccd?: string;
    tenant_date_of_birth?: string;
    tenant_address?: string;
    tenant_phone?: string;
  }): Promise<Contract> {
    const response = await fetch(`${API_BASE}/contracts`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(contractData),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ detail: response.statusText }));
      throw new Error(errorData.detail || `Failed to create contract: ${response.statusText}`);
    }

    const data = await response.json();
    return data;
  }

  /**
   * Get all contracts for current user
   */
  async getContracts(params?: {
    role?: string;
    account_type?: string;
    status?: string;
    limit?: number;
    offset?: number;
  }): Promise<Contract[]> {
    const queryParams = new URLSearchParams();
    if (params?.role) queryParams.append('role', params.role);
    if (params?.account_type) queryParams.append('account_type', params.account_type);
    if (params?.status) queryParams.append('status', params.status);
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    if (params?.offset) queryParams.append('offset', params.offset.toString());
    
    const url = `${API_BASE}/contracts${queryParams.toString() ? '?' + queryParams.toString() : ''}`;
    
    // Debug logging
    const token = localStorage.getItem('auth_token') || localStorage.getItem('token');
    console.log(`[ContractApi.getContracts] Token present: ${!!token}, URL: ${url}, API_BASE: ${API_BASE}`);
    
    let response;
    try {
      response = await fetch(url, {
        headers: this.getAuthHeaders(),
      });
    } catch (fetchError: any) {
      console.error(`[ContractApi.getContracts] Network error:`, fetchError);
      console.error(`  - URL: ${url}`);
      console.error(`  - Error: ${fetchError.message}`);
      console.error(`  - This might be CORS, backend offline, or network issue`);
      throw new Error(`Network error: ${fetchError.message}`);
    }

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ detail: response.statusText }));
      console.error(`[ContractApi.getContracts] Error ${response.status}:`, errorData);
      throw new Error(`Failed to fetch contracts: ${response.statusText} - ${errorData.detail || ''}`);
    }

    return response.json();
  }

  /**
   * Hợp đồng đang hiệu lực của phòng khách thuê QR (app native).
   */
  async getTenantCurrentContract(): Promise<Contract | null> {
    const response = await fetch(`${API_BASE}/contracts/tenant/current`, {
      headers: this.getAuthHeaders(),
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch current tenant contract: ${response.statusText}`);
    }

    const data = await response.json();
    return (data?.contract as Contract | null) ?? null;
  }

  /**
   * Get all contracts for a specific property (public endpoint, no auth required)
   */
  async getContractsByProperty(propertyId: number): Promise<{ contracts: Contract[] }> {
    const response = await fetch(`${API_BASE}/contracts/property/${propertyId}`, {
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch contracts for property: ${response.statusText}`);
    }

    return response.json();
  }

  /**
   * Get a single contract by ID
   */
  async getContract(contractId: number): Promise<Contract> {
    const response = await fetch(`${API_BASE}/contracts/${contractId}`, {
      headers: this.getAuthHeaders(),
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch contract: ${response.statusText}`);
    }

    const data = await response.json();
    console.log('[contractApi.getContract] Raw API response:', { contractId, data });
    return data;
  }

  async downloadContractPdf(contractId: number): Promise<Blob> {
    const response = await fetch(`${API_BASE}/contracts/${contractId}/pdf`, {
      headers: this.getAuthHeaders(),
    });

    if (!response.ok) {
      const errorText = await response.text();
      let message = 'Không tải được PDF hợp đồng';
      try {
        const parsed = JSON.parse(errorText);
        if (typeof parsed?.detail === 'string' && parsed.detail.trim()) {
          message = parsed.detail;
        }
      } catch {
        if (errorText.trim()) message = errorText;
      }
      throw new Error(message);
    }

    return response.blob();
  }

  /**
   * Get workflow status for a contract
   */
  async getWorkflowStatus(contractId: number): Promise<WorkflowStatus> {
    const response = await fetch(`${API_BASE}/contracts/${contractId}/workflow/status`, {
      headers: this.getAuthHeaders(),
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch workflow status: ${response.statusText}`);
    }

    const data = await response.json();
    console.log('[contractApi.getWorkflowStatus] Raw API response:', { contractId, data });
    return data;
  }

  /**
   * Get contract payments
   * Note: Contract payments endpoint has been removed. Returning empty array.
   */
  async getContractPayments(): Promise<ContractPayment[]> {
    // Payment system has been removed - return empty array
    // Contracts are now displayed based on status, not payment history
    return [];
  }

  /**
   * Sign contract
   */
  async signContract(contractId: number, role: string): Promise<Contract> {
    const response = await fetch(`${API_BASE}/contracts/${contractId}/sign?role=${role}`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
    });

    if (!response.ok) {
      throw new Error(`Failed to sign contract: ${response.statusText}`);
    }

    const data = await response.json();
    return data.contract;
  }

  /**
   * Start check-in process
   */
  async startCheckIn(contractId: number): Promise<Contract> {
    const response = await fetch(`${API_BASE}/contracts/${contractId}/check-in/start`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
    });

    if (!response.ok) {
      throw new Error(`Failed to start check-in: ${response.statusText}`);
    }

    const data = await response.json();
    return data.contract;
  }

  /**
   * Update check-in sub-step
   */
  async updateCheckInSubStep(
    contractId: number,
    step: string,
    completed: boolean
  ): Promise<void> {
    const response = await fetch(`${API_BASE}/contracts/${contractId}/check-in/sub-step`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify({ step, completed }),
    });

    if (!response.ok) {
      throw new Error(`Failed to update check-in sub-step: ${response.statusText}`);
    }
  }

  /**
   * Update check-in metadata
   */
  async updateCheckInMetadata(
    contractId: number,
    metadata: Record<string, any>
  ): Promise<{ success: boolean; data?: any; error_message?: string }> {
    try {
      const headers = this.getAuthHeaders();
      const response = await fetch(`${API_BASE}/contracts/${contractId}/check-in/metadata`, {
        method: 'PATCH',
        headers,
        body: JSON.stringify(metadata),
      });

      if (!response.ok) {
        if (response.status === 401) {
          await this.handleAuthError(response);
        }
        
        const errorText = await response.text();
        let errorMessage = errorText || `Failed to update check-in metadata: ${response.statusText}`;
        
        try {
          const errorJson = JSON.parse(errorText);
          errorMessage = errorJson.detail || errorMessage;
        } catch {
          // Keep original errorText
        }
        
        return {
          success: false,
          error_message: errorMessage
        };
      }

      const data = await response.json();
      return { success: true, data };
    } catch (error: any) {
      return {
        success: false,
        error_message: error?.message || 'Failed to update check-in metadata'
      };
    }
  }

  /**
   * Verify QR code for check-in
   */
  async verifyQRCode(
    contractId: number,
    qrData: string,
    verifiedBy: 'tenant' | 'landlord' | 'business',
    sessionId?: string,
    requestId?: string,
    deviceInfo?: Record<string, any>,
    locationInfo?: Record<string, any>
  ): Promise<{ success: boolean; data?: any; error_message?: string }> {
    try {
      const headers = this.getAuthHeaders();
      
      const response = await fetch(`${API_BASE}/contracts/${contractId}/workflow/check-in/verify-qr`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          qr_data: qrData,
          verified_by: verifiedBy,
          session_id: sessionId,
          request_id: requestId,
          device_info: deviceInfo,
          location_info: locationInfo
        }),
      });

      if (!response.ok) {
        if (response.status === 401) {
          // Handle authentication error
          await this.handleAuthError(response);
        }
        
        const errorText = await response.text();
        let errorMessage = errorText || `Failed to verify QR code: ${response.statusText}`;
        
        // Try to parse JSON error
        try {
          const errorJson = JSON.parse(errorText);
          errorMessage = errorJson.detail || errorMessage;
        } catch {
          // Keep original errorText
        }
        
        return {
          success: false,
          error_message: errorMessage
        };
      }

      const data = await response.json();
      return { success: true, data };
    } catch (error: any) {
      return {
        success: false,
        error_message: error?.message || 'Failed to verify QR code'
      };
    }
  }

  /**
   * Complete check-in
   */
  async completeCheckIn(contractId: number): Promise<Contract> {
    const response = await fetch(`${API_BASE}/contracts/${contractId}/check-in/complete`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
    });

    if (!response.ok) {
      throw new Error(`Failed to complete check-in: ${response.statusText}`);
    }

    const data = await response.json();
    return data.contract;
  }

  /**
   * Start key handover process
   */
  async startKeyHandover(contractId: number, notes?: string): Promise<Contract> {
    const response = await fetch(`${API_BASE}/contracts/${contractId}/key-handover/start`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify({ notes: notes || null }),
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Failed to start key handover: ${response.statusText} - ${errorText}`);
    }

    const data = await response.json();
    return data.contract;
  }

  /**
   * Confirm key handover
   */
  async confirmKeyHandover(
    contractId: number,
    role: string,
    notes?: string
  ): Promise<Contract> {
    const response = await fetch(`${API_BASE}/contracts/${contractId}/key-handover/confirm`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify({ role, notes: notes || null }),
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Failed to confirm key handover: ${response.statusText} - ${errorText}`);
    }

    const data = await response.json();
    return data.contract;
  }

  /**
   * Get all comments for a contract
   */
  async getContractComments(contractId: number): Promise<any[]> {
    const response = await fetch(`${API_BASE}/contracts/${contractId}/comments`, {
      headers: this.getAuthHeaders(),
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch contract comments: ${response.statusText}`);
    }

    return response.json();
  }

  /**
   * Create a new comment for a contract
   */
  async createContractComment(contractId: number, commentData: { content: string; parent_id?: number }): Promise<any> {
    const response = await fetch(`${API_BASE}/contracts/${contractId}/comments`, {
      method: 'POST',
      headers: {
        ...this.getAuthHeaders(),
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(commentData),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ message: response.statusText }));
      throw new Error(errorData.message || `Failed to create comment: ${response.statusText}`);
    }

    return response.json();
  }

  /**
   * Update a comment (mark as read or edit content)
   */
  async updateContractComment(contractId: number, commentId: number, updateData: { is_read?: boolean; content?: string }): Promise<any> {
    const response = await fetch(`${API_BASE}/contracts/${contractId}/comments/${commentId}`, {
      method: 'PATCH',
      headers: {
        ...this.getAuthHeaders(),
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(updateData),
    });

    if (!response.ok) {
      throw new Error(`Failed to update comment: ${response.statusText}`);
    }

    return response.json();
  }

  /**
   * Get replies to a comment
   */
  async getCommentReplies(contractId: number, commentId: number): Promise<any[]> {
    const response = await fetch(`${API_BASE}/contracts/${contractId}/comments/${commentId}/replies`, {
      headers: this.getAuthHeaders(),
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch comment replies: ${response.statusText}`);
    }

    return response.json();
  }

  /**
   * Get unread comments count for a contract
   */
  async getUnreadCommentsCount(contractId: number): Promise<number> {
    try {
      const response = await fetch(`${API_BASE}/contracts/${contractId}/comments/unread-count`, {
        headers: this.getAuthHeaders(),
      });

      if (!response.ok) {
        // If endpoint doesn't exist or fails, return 0
        console.warn(`Failed to fetch unread comments count: ${response.statusText}`);
        return 0;
      }

      const data = await response.json();
      return data.count || 0;
    } catch (error) {
      console.error('Error fetching unread comments count:', error);
      return 0;
    }
  }

  /**
   * Confirm contract signature (no OTP — user confirms in app)
   */
  async confirmSignature(
    contractId: number,
    role: string,
    signatureImage?: string,
  ): Promise<{ success: boolean; both_signed: boolean; contract: Contract }> {
    const response = await fetch(`${API_BASE}/contracts/${contractId}/signature/confirm`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify({ role, signature_image: signatureImage || null }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ message: response.statusText }));
      throw new Error(errorData.message || `Failed to confirm signature: ${response.statusText}`);
    }

    const data = await response.json();
    return {
      success: data.success || true,
      both_signed: data.both_signed || false,
      contract: data.contract
    };
  }

  /**
   * Request contract termination
   */
  async requestTermination(
    contractId: number,
    reason: string,
    terminationDate?: string
  ): Promise<{
    success: boolean;
    message: string;
    contract: Contract;
    deposit_refund_amount: number;
    compensation_amount: number;
    is_early_termination: boolean;
  }> {
    const response = await fetch(`${API_BASE}/contracts/${contractId}/termination/request`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify({
        reason,
        termination_date: terminationDate || null
      }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ message: response.statusText }));
      throw new Error(errorData.detail || errorData.message || `Failed to request termination: ${response.statusText}`);
    }

    return response.json();
  }

  /**
   * Approve contract termination
   */
  async approveTermination(contractId: number): Promise<{
    success: boolean;
    message: string;
    contract: Contract;
  }> {
    const response = await fetch(`${API_BASE}/contracts/${contractId}/termination/approve`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ message: response.statusText }));
      throw new Error(errorData.detail || errorData.message || `Failed to approve termination: ${response.statusText}`);
    }

    return response.json();
  }

  /**
   * Complete contract termination and process refund
   */
  async completeTermination(contractId: number): Promise<{
    success: boolean;
    message: string;
    contract: Contract;
    refund_result: any;
  }> {
    const response = await fetch(`${API_BASE}/contracts/${contractId}/termination/complete`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ message: response.statusText }));
      throw new Error(errorData.detail || errorData.message || `Failed to complete termination: ${response.statusText}`);
    }

    return response.json();
  }

  /**
   * Get termination status for a contract
   */
  async getTerminationStatus(contractId: number): Promise<{
    contract_id: number;
    termination_status: string | null;
    termination_requested_at: string | null;
    termination_approved_at: string | null;
    is_early_termination: boolean;
    deposit_refund_amount: number;
    compensation_amount: number;
    deposit_refund_status: string | null;
    termination_reason: string | null;
    actual_end_date: string | null;
  }> {
    const response = await fetch(`${API_BASE}/contracts/${contractId}/termination/status`, {
      headers: this.getAuthHeaders(),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ message: response.statusText }));
      throw new Error(errorData.detail || errorData.message || `Failed to get termination status: ${response.statusText}`);
    }

    return response.json();
  }

  /**
   * Create an order from a contract for payment processing
   */
  async createOrderFromContract(contractId: number): Promise<{
    order_id: number;
    total_amount: number;
    deposit_amount: number;
    first_month_amount: number;
    platform_fee: number;
    message: string;
  }> {
    const response = await fetch(`${API_BASE}/contracts/${contractId}/create-order`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ message: response.statusText }));
      throw new Error(errorData.detail || errorData.message || `Failed to create order: ${response.statusText}`);
    }

    return response.json();
  }

  /**
   * Cancel contract (for schedule rejection)
   */
  async cancelContract(contractId: number, reason?: string): Promise<Contract> {
    const formData = new FormData();
    formData.append('reason', reason || 'Cancelled by landlord');

    const token = localStorage.getItem('auth_token') || localStorage.getItem('token');
    const headers: HeadersInit = {};
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const response = await fetch(`${API_BASE}/contracts/${contractId}/cancel`, {
      method: 'POST',
      headers,
      body: formData,
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ message: response.statusText }));
      throw new Error(errorData.detail || errorData.message || `Failed to cancel contract: ${response.statusText}`);
    }

    return response.json();
  }

  async scheduleContractCancel(
    contractId: number,
    cancelDate: string,
    reason?: string,
  ): Promise<{
    applied: boolean;
    cancel_date: string;
    message: string;
    contract: Contract;
  }> {
    const response = await fetch(`${API_BASE}/contracts/${contractId}/schedule-cancel`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify({
        cancel_date: cancelDate,
        reason: reason || 'Khách thuê đặt lịch hủy hợp đồng từ app',
      }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ message: response.statusText }));
      throw new Error(
        errorData.detail || errorData.message || `Failed to schedule contract cancel: ${response.statusText}`,
      );
    }

    return response.json();
  }
}

export const contractApi = new ContractApiService();

// Type alias for backward compatibility
export type ContractSummary = Contract & {
  property?: {
    id: number;
    title: string;
    type: string;
  };
  tenant?: {
    id: number;
    full_name?: string;
    username?: string;
    email?: string;
  };
  landlord?: {
    id: number;
    full_name?: string;
    username?: string;
    email?: string;
  };
  broker?: {
    id: number;
    full_name?: string;
    username?: string;
    email?: string;
  };
  documents?: Array<{
    id: number;
    name: string;
    file_path: string;
    file_type?: string;
    file_size?: number;
    created_at: string;
  }>;
  next_payment_date?: string;
  progress?: number;
  // Termination fields
  termination_status?: string | null;
  termination_requested_at?: string | null;
  termination_reason?: string | null;
  is_early_termination?: boolean;
  deposit_refund_amount?: number;
  compensation_amount?: number;
};

// Add termination methods to ContractApiService class - insert before closing brace of class
// These need to be added inside the class definition after the last method

